import { useCallback, useState } from 'react';
import { AuthenticationProvider, ThirdPartyProvider } from 'graphql-ts-types';

import { ThirdPartyProviderString } from 'common/enums/ThirdPartyProvider';
import { dispatchTrackingEventGA4 } from 'common/tracking/dispatchTrackingEventGA4';
import { Event } from 'common/types/auth';
import { Element } from 'common/types/tracking';
import { captureException } from 'common/utils/error-reporter';
import { useLoadAppleClient } from './load-apple-client';
import type { PerformLogin } from './third-party-login';

interface UseAppleSignInParams {
  clientId: string;
  stateToken: string;
  onAppleAuth: PerformLogin;
  setCommonError: () => void;
  placement?: string;
}

const SCOPE = 'name email';
const POPUP_CLOSED_BY_USER_ERROR = 'popup_closed_by_user';

const dispatchSignInClickEvent = (placement?: string) =>
  dispatchTrackingEventGA4({
    event: Event.LoginFormClick,
    element: Element.Button,
    button_text: ThirdPartyProviderString.apple,
    provider: AuthenticationProvider.Apple,
    ...(placement && { placement }),
  });

export const useAppleSignIn = ({
  clientId,
  placement,
  stateToken,
  onAppleAuth,
  setCommonError,
}: UseAppleSignInParams) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleFailure = (errorMessage: string) => {
    setCommonError();
    captureException(new Error(errorMessage));
  };

  const signIn = useCallback(async () => {
    dispatchSignInClickEvent(placement);
    setIsLoading(true);

    try {
      const data = await window.AppleID.auth.signIn();

      if (data.authorization.id_token) {
        if (data.authorization.state === stateToken) {
          await onAppleAuth(ThirdPartyProvider.Apple, data.authorization.id_token, data.user?.name);
          setIsLoading(false);

          return;
        } else {
          handleFailure('Apple sign in failed with error: state mismatch');
        }
      } else {
        handleFailure('Apple sign in failed with error: missing id_token');
      }
    } catch (error) {
      if (error?.error !== POPUP_CLOSED_BY_USER_ERROR) {
        handleFailure(`Apple sign in failed with error: ${error?.error}`);
      }
    }
    setIsLoading(false);
  }, []);

  const onAppleClientLoad = useCallback(() => {
    window.AppleID.auth.init({
      redirectURI: window.location.origin,
      state: stateToken,
      usePopup: true,
      scope: SCOPE,
      clientId,
    });
  }, []);

  useLoadAppleClient(onAppleClientLoad);

  return { isLoading, signIn };
};
